import React, { memo } from 'react';
import axios from 'axios';
import styled from 'styled-components/macro';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from 'controller/types';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { BranchDetails } from './BranchDetails';
import { AcademicYearForm } from 'features/BranchSetup/components/DesktopComponents/Forms/AcademicYearForm';
import { ClassForm } from 'features/BranchSetup/components/DesktopComponents/Forms/ClassForm';
import { SubjectForm } from 'features/BranchSetup/components/DesktopComponents/Forms/SubjectForm';
import { BranchShiftForm } from 'features/BranchSetup/components/DesktopComponents/Forms/BranchShiftForm';
import { BranchLeaveForm } from 'features/BranchSetup/components/DesktopComponents/Forms/BranchLeaveForm';
import { Staff } from 'features/UserSetup/components/DesktopComponents/Forms/Staff';
import { CreateUpdate as Student } from 'features/UserSetup/components/DesktopComponents/Forms/Student/CreateUpdate';
import FinishSetup from './FinishSetup';
import { useBranchSetupSlice } from 'features/BranchSetup/slice';
import { useAuthenticationSlice } from 'features/Authentication/slice';
import { getListData } from './getListData';
import { Table } from 'core/components/Table';
import { getTotalEnteriesCount } from 'core/utils/constants';
import { Button } from 'core/components/Button';
import { SetupLoader } from 'core/components/SetupComponents/SetupLoader';
import Logo from 'assets/images/Logo.svg';
import { HandSpan } from 'features/Authentication/components/DesktopComponent';
import SuccessCheckFill from 'assets/icons/SuccessCheckFill.svg';
import { ReactComponent as SwitchRole } from 'assets/icons/SwitchRole.svg';
import { ReactComponent as Logout } from 'assets/icons/Logout.svg';
import { ImageComponent } from 'core/components/ImageComponent';
import History from "core/utils/history";


const forms = [
  { component: BranchDetails, title: 'Branch Details', url: 'branch' },
  { component: AcademicYearForm, title: 'Academic Year', url: 'academicYear' },
  { component: ClassForm, title: 'Class', url: 'class' },
  { component: SubjectForm, title: 'Subject', url: 'subject' },
  {
    component: BranchLeaveForm,
    title: 'Staff Leave Type',
    url: 'staffLeaveType',
  },
  { component: BranchShiftForm, title: 'Branch Shift', url: 'branchShift' },
  { component: Staff, title: 'Staff', url: 'staff' },
  // { component: Student, title: 'Student', url: 'student' },
  { component: FinishSetup, title: 'Finish Setup' },
];

interface Props {}

interface CompletedProgressBarProps {
  width?: number;
}

const InitialSetup = memo((props: Props) => {
  const dispatch = useDispatch();
  const { actions } = useBranchSetupSlice();
  const { actions: authenticationActions } = useAuthenticationSlice();
  const state: any = useSelector((state: RootState) => state?.branchSetup);
  const authData = useAuth();
  const [isMenuVisible, setIsMenuVisible] = React.useState<boolean>(false);
  const refMenu = React.useRef<HTMLDivElement>(null);

  const [currentStep, setCurrentStep] = React.useState(0);

  const totalSteps = forms.length;
  let cancelTokenSource;

  const moveToNextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const moveToBackStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const CurrentForm = forms[currentStep].component;
  const formTitle = forms[currentStep].title;
  const progress = (25 / forms?.length) * currentStep; // 25 -> fixed width in vw units


  React.useEffect(() => {
    dispatch(actions.clearBranchData());
    if (
      currentStep > 0 &&
      currentStep < forms?.length - 1 &&
      authData?.BRANCH_ID
    ) {
      dispatch(
        actions.fetchBranchSetupListStart({
          ID: authData?.BRANCH_ID as number,
          URL: forms[currentStep]?.url as string,
          ACADEMIC_YEAR_ID: authData?.SELECTED_ACADEMIC_YEAR?.ACADEMIC_YEAR_ID,
        }),
      );
    }
  }, [currentStep]);


  window.addEventListener('popstate', () => {
    if (authData?.ALLOW_INITIAL_SETUP_ACCESS) {
      dispatch(authenticationActions.updateAuthData({
        ...state?.authData,
        ALLOW_INITIAL_SETUP_ACCESS: false
      }));
      History.navigate('/dashboard')
    }
  });
  


  const showTable =
    currentStep !== forms?.length - 1 &&
    (forms[currentStep]?.url === 'student'
      ? state?.branchDataList && state?.branchDataList?.Students?.length > 0
      : state?.branchDataList && state?.branchDataList?.length > 0);


  return ['SUPERADMIN', 'ADMIN']?.includes(
    authData?.STAFF_ROLE?.STAFF_ROLE_NAME?.toLocaleUpperCase() as string,
  ) ? (
    <>
      <div className="w-screen h-screen bg-[#F3F4FF]">
        <div className="h-[10vh] py-4 px-5 w-full flex justify-between items-center">
          <div className="gap-5 flex items-center">
            <img
              className="w-[3vw] h-[3vw] object-contain mx-auto"
              src={Logo}
              alt="SooMuch Logo"
            />
            <p className="text-[##242A34] font-bold text-xl">
              Initial Branch Setup
            </p>
          </div>
          <div>
          <div
        className={`${!isMenuVisible && 'hidden'
          } absolute right-1 -top-7 h-fit w-[12vw] bg-white z-50 translate-x-[-1vw] translate-y-[12vh] rounded-xl shadow-lg border-[1px] border-neutral-40 border-solid`}
        ref={refMenu}
      >
        {!state.isLoggingOut ? (
          <div>
          <div
            className="w-full min-h-[4vh] p-[1vw] flex items-center hover:bg-neutral-30 cursor-pointer rounded-xl"
            onClick={e => {
              e.preventDefault();
              if (typeof cancelTokenSource != typeof undefined) {
                cancelTokenSource.cancel();
              }
              cancelTokenSource = axios.CancelToken.source();
              authData?.USER_LOGIN_ID &&
                dispatch(
                  authenticationActions.logoutStart({
                    USER_LOGIN_ID: authData?.USER_LOGIN_ID,
                    cancelToken: cancelTokenSource,
                  }),
                );
            }}
          >
            <Logout className="w-[2vw] h-2[vw] mr-[0.4vw]" />
            <h1 className="text-md">Logout</h1>
          </div>
          {authData?.ALLOW_INITIAL_SETUP_ACCESS && (
            <div 
            className="w-full min-h-[4vh] p-[1vw] flex items-center justify-center  hover:bg-neutral-30 cursor-pointer rounded-xl"
            onClick={() => {
              dispatch(authenticationActions.updateAuthData({
                ...state?.authData,
                ALLOW_INITIAL_SETUP_ACCESS: false
              }));
              History.navigate('/dashboard');
            }}>Enter Dashboard</div>
          )}
          </div>
        ) : (
          <SetupLoader width="w-12" height="h-12" />
        )}
      </div>
          <div onClick={() => setIsMenuVisible(!isMenuVisible)} className="cursor-pointer w-14 h-14 rounded-full bg-white border-2 border-solid border-[rgba(255, 255, 255, 0.8)]">
            <div className="w-[10px] h-[10px] rounded-full bg-secondary-green relative left-[78%] top-[3%]"></div>
            <div className="flex justify-center items-center">
              {/* <img
                src={authData?.USER_PHOTO_URL}
                alt="user-profile"
                className="rounded-xl w-[70%] h-[70%]"
              /> */}
              <ImageComponent url={authData?.USER_PHOTO_URL || ''} alt='user' width={70} height={30} className='mb-5 w-8 h-8' />
            </div>
          </div>
          </div>
        </div>
        <div className="h-[90vh]">
          <div className="bg-gradient-to-r from-[#DB3649] to-[#8431D6] h-[25vh] w-full px-8">
            <div className="pt-6 flex justify-between items-center">
              <div>
                <p className="font-bold text-white text-2xl mb-1">
                  Welcome, {`{`} {authData?.STAFF_ROLE?.STAFF_ROLE_NAME}
                  {` }`} <HandSpan>👋</HandSpan>
                </p>
                <p className="text-white text-md">Let's get you onboarded</p>
              </div>
              <div className="w-[25vw]">
                <div className="mb-2 flex justify-between items-center">
                  <p className="text-white text-lg font-semibold">{formTitle}</p>
                  <p className="text-white text-md font-semibold">
                    {currentStep + 1}/{forms?.length}
                  </p>
                </div>
                <div
                  className="w-[25vw] h-[1.28vh] bg-gray-200 rounded-full overflow-hidden border border-gray-300"
                  style={{ backgroundColor: '#F3F4FF' }}
                >
                  <CompletedProgressBar width={progress} />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white h-[75vh] w-[87vw] flex rounded-xl relative bottom-[12vh] mx-auto shadow-xl">
            <div className="w-1/6 pl-[2%] pt-[3%] h-full border-r border-gray-300 flex flex-col items-center overflow-y-auto">
              {forms?.map((eachForm, index) => (
                <div
                  className={`${
                    index !== currentStep && 'hover:bg-primary-10'
                  } w-full pl-[5%] pr-[4%] py-[8%] border-l-2 ${
                    index === currentStep
                      ? 'border-l-primary-main'
                      : 'border-l-neutral-30'
                  } ${
                    index === currentStep && 'font-semibold'
                  } rounded-tr-md rounded-br-md text-[0.95rem]
                }`}
                >
                  <div className="flex items-center gap-2">
                    {index < currentStep && (
                      <img
                        src={SuccessCheckFill}
                        className="w-[1vw] h-[1vw]"
                        alt="success-check"
                      />
                    )}
                    {eachForm?.title}
                  </div>
                </div>
              ))}
            </div>
            <div className="w-5/6 h-full flex flex-col justify-end pt-2 pr-1">
              <div className='p-5 overflow-y-auto'>
              <CurrentForm onNext={moveToNextStep} />
              {showTable ? (
                <Table
                  pageIndex={state?.pageIndex}
                  pageSize={state?.pageSize}
                  totalEntries={getTotalEnteriesCount(state?.listResponse)}
                  columns={getListData(forms[currentStep]?.url as string)}
                  data={
                    forms[currentStep]?.url === 'student'
                      ? state?.branchDataList?.Students
                      : state?.branchDataList || []
                  }
                  onRowClick={() => {}}
                />
              ) : (
                <></>
              )}
              </div>
              <div className="flex justify-between items-center w-full bg-neutral-30 px-3 py-2 border-t">
                <Button
                  type="outlined"
                  width="w-16"
                  height="h-10"
                  label="Back"
                  disabled={currentStep === 0}
                  onClick={moveToBackStep}
                />
                <button
                  className="px-5 py-2 bg-gradient-to-r from-[#DB3649] to-[#8431D6] rounded-md text-white"
                  disabled={
                    currentStep === 0
                      ? false
                      : !(forms[currentStep]?.url === 'student'
                          ? state?.branchDataList?.Students?.length
                          : state?.branchDataList?.length)
                  }
                  onClick={moveToNextStep}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
     
    </>
  ) : (
    <div className="flex flex-col h-screen justify-center items-center">
      <h1 className="text-[3vw] mb-5 font-bold">Please Contact Admin</h1>
      {!state?.isLoggingOut ? (
        <Button
          type="primary"
          label="Logout"
          onClick={e => {
            e.preventDefault();
            if (typeof cancelTokenSource != typeof undefined) {
              cancelTokenSource.cancel();
            }
            cancelTokenSource = axios.CancelToken.source();

            dispatch(authenticationActions.updateAuthData({
              ...state?.authData,
              ALLOW_INITIAL_SETUP_ACCESS: false
            }));

            authData?.USER_LOGIN_ID &&
              dispatch(
                authenticationActions.logoutStart({
                  USER_LOGIN_ID: authData?.USER_LOGIN_ID,
                  cancelToken: cancelTokenSource,
                }),
              );
          }}
        />
      ) : (
        <SetupLoader />
      )}
    </div>
  );
});

export default InitialSetup;

const CompletedProgressBar = styled.div<CompletedProgressBarProps>`
  height: 100%;
  background: linear-gradient(to right, #db3649, #8431d6);
  border-radius: 2px;
  width: ${props => (props.width ? `${props.width}vw` : '0%')};
`;
